import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TermLog {
  id: string;
  term_id: string;
  action: 'create' | 'edit' | 'discard' | 'publish';
  performed_by_user_id: string | null;
  performed_by_user_name: string | null;
  performed_at: string;
  details: Record<string, any> | null;
}

export interface Term {
  id: string;
  content: string;
  created_at: string;
  updated_at: string | null;
  pending_activation: boolean;
  created_by_user_id: string | null;
  created_by_user_name: string | null;
  logs?: TermLog[];
}

export const useTermsOfUse = () => {
  return useQuery({
    queryKey: ['terms-of-use'],
    queryFn: async () => {
      // Buscar termo ativo (não pendente e não arquivado)
      const { data: activeTerm, error: activeError } = await supabase
        .from('terms_of_use')
        .select('*')
        .is('updated_at', null)
        .eq('pending_activation', false)
        .maybeSingle();
      
      if (activeError) throw activeError;
      
      // Buscar termos pendentes (pending_activation = true)
      const { data: pendingTerms, error: pendingError } = await supabase
        .from('terms_of_use')
        .select('*')
        .eq('pending_activation', true)
        .order('created_at', { ascending: false });
      
      if (pendingError) throw pendingError;
      
      // Buscar histórico (arquivados)
      const { data: history, error: historyError } = await supabase
        .from('terms_of_use')
        .select('*')
        .not('updated_at', 'is', null)
        .order('created_at', { ascending: false });
      
      if (historyError) throw historyError;

      // Buscar todos os logs
      const { data: allLogs, error: logsError } = await supabase
        .from('terms_of_use_logs' as any)
        .select('*')
        .order('performed_at', { ascending: false });
      
      if (logsError) throw logsError;

      const logs = (allLogs || []) as unknown as TermLog[];

      // Associar logs aos termos pendentes
      const pendingWithLogs = (pendingTerms || []).map(term => ({
        ...term,
        logs: logs.filter(log => log.term_id === term.id)
      }));

      // Associar logs aos termos do histórico
      const historyWithLogs = (history || []).map(term => ({
        ...term,
        logs: logs.filter(log => log.term_id === term.id)
      }));
      
      return { 
        activeTerm: activeTerm as Term | null,
        pendingTerms: pendingWithLogs as Term[],
        history: historyWithLogs as Term[] 
      };
    }
  });
};
